import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import LinkExt from '@tiptap/extension-link';
import ImageExt from '@tiptap/extension-image';
import {
  Bold, Italic, List, ListOrdered, Heading2, Heading3, Minus, Undo, Redo, Code,
  Link as LinkIcon, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight,
} from 'lucide-react';
import { uploadArticleImage } from '../utils/api';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ToolbarBtn = ({ onClick, active, title, children, testId }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={title}
    data-testid={testId}
    className={`p-1.5 rounded text-sm transition-colors ${
      active
        ? 'bg-primary text-primary-foreground'
        : 'hover:bg-muted/80 text-foreground'
    }`}
  >
    {children}
  </button>
);

const Divider = () => <span className="w-px h-5 bg-border mx-0.5 self-center shrink-0" />;

const ArticleRichEditor = ({
  value,
  onChange,
  placeholder = 'Escribe el contenido del artículo...',
  minHeight = 380,
}) => {
  const prevValueRef = useRef(value);
  const fileRef = useRef();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder }),
      LinkExt.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
      ImageExt.configure({ HTMLAttributes: { class: 'rounded-lg my-3 max-w-full' } }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      prevValueRef.current = html;
      onChange(html);
    },
  });

  useEffect(() => {
    if (editor && value !== prevValueRef.current) {
      editor.commands.setContent(value || '', false);
      prevValueRef.current = value;
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL del enlace', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadArticleImage(fd);
      const url = res.data.url.startsWith('/api') ? `${API_BASE}${res.data.url}` : res.data.url;
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      toast.success('Imagen insertada');
    } catch {
      toast.error('Error al subir la imagen');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden focus-within:ring-1 focus-within:ring-ring bg-background" data-testid="article-rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b bg-muted/20 sticky top-0 z-10">
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Negrita (Ctrl+B)" testId="rt-bold">
          <Bold className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Cursiva (Ctrl+I)" testId="rt-italic">
          <Italic className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Código inline">
          <Code className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Título H2">
          <Heading2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Título H3">
          <Heading3 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Lista de viñetas">
          <List className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Lista numerada">
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Cita">
          <span className="font-serif text-base leading-none">"</span>
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Línea divisoria">
          <Minus className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={setLink} active={editor.isActive('link')} title="Insertar enlace" testId="rt-link">
          <LinkIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => fileRef.current?.click()} title="Insertar imagen" testId="rt-image">
          <ImageIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Deshacer (Ctrl+Z)">
          <Undo className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Rehacer (Ctrl+Y)">
          <Redo className="w-3.5 h-3.5" />
        </ToolbarBtn>
      </div>
      <EditorContent
        editor={editor}
        className="rich-editor px-4 py-3"
        style={{ minHeight: `${minHeight}px` }}
      />
    </div>
  );
};

export default ArticleRichEditor;
